import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const USERNAME_RE = /^[a-z0-9_]{3,24}$/;

export default function UsernameSetupPage() {
  const navigate = useNavigate();
  const { currentUser, userToken, setCurrentUser } = useGame();

  const [username, setUsername]     = useState("");
  const [status, setStatus]         = useState(null); // null | "checking" | "available" | "taken" | "invalid"
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // If user already has a username, skip this page
  useEffect(() => {
    if (currentUser && !currentUser.needs_username) navigate("/", { replace: true });
  }, [currentUser, navigate]);

  const handleChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setUsername(val);
    setStatus(null);

    clearTimeout(debounceRef.current);
    if (val.length < 3) return;
    if (!USERNAME_RE.test(val)) { setStatus("invalid"); return; }

    setStatus("checking");
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/auth/username/check`, { params: { username: val } });
        setStatus(data.available ? "available" : "taken");
      } catch {
        setStatus(null);
      }
    }, 450);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!USERNAME_RE.test(username)) { toast.error("اسم المستخدم يجب أن يكون بالإنجليزي (3-24 حرف)"); return; }
    if (status === "taken")          { toast.error("هذا الاسم مستخدم — جرّب اسماً آخر"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/username/setup`,
        { username },
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
      setCurrentUser(data.user);
      localStorage.setItem("hujjah_user", JSON.stringify(data.user));
      toast.success("تم! مرحباً بك في حُجّة 🎉");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      toast.error(msg);
      if (err?.response?.status === 409) setStatus("taken");
    } finally {
      setSubmitting(false);
    }
  };

  const statusIcon =
    status === "checking"  ? <span style={{ color: "rgba(241,225,148,0.5)" }}>...</span>  :
    status === "available" ? <span style={{ color: "#4ade80" }}>✓ متاح</span>              :
    status === "taken"     ? <span style={{ color: "#f87171" }}>✕ مستخدم</span>            :
    status === "invalid"   ? <span style={{ color: "#f87171" }}>✕ إنجليزي فقط</span>      :
    null;

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg,#3a0a0e 0%,#1a0507 100%)",
      fontFamily: "'Tajawal','Cairo',sans-serif", direction: "rtl", padding: "24px",
    }}>
      <div style={{
        width: "100%", maxWidth: "400px",
        background: "rgba(255,255,255,0.04)", border: "1px solid rgba(241,225,148,0.18)",
        borderRadius: "24px", padding: "40px 32px", textAlign: "center",
      }}>
        {/* Logo mark */}
        <div style={{ fontSize: "2rem", fontWeight: 900, color: "#F1E194", marginBottom: "8px", letterSpacing: "0.04em" }}>
          حُجّة
        </div>
        <h1 style={{ color: "#FDF6E3", fontWeight: 800, fontSize: "1.25rem", margin: "0 0 6px" }}>
          اختر اسم المستخدم
        </h1>
        <p style={{ color: "rgba(241,225,148,0.55)", fontSize: "0.82rem", margin: "0 0 32px", lineHeight: 1.6 }}>
          يكون بالإنجليزي فقط — هذا الاسم الذي سيبحث عنك به أصدقاؤك
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ position: "relative", marginBottom: "8px" }}>
            <span style={{
              position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)",
              color: "rgba(241,225,148,0.45)", fontSize: "1rem", pointerEvents: "none",
            }}>@</span>
            <input
              type="text"
              value={username}
              onChange={handleChange}
              maxLength={24}
              placeholder="rakan11"
              autoFocus
              disabled={submitting}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "13px 36px 13px 14px", borderRadius: "12px",
                background: "rgba(255,255,255,0.07)", border: "1.5px solid rgba(241,225,148,0.25)",
                color: "#FDF6E3", fontSize: "1rem", fontFamily: "inherit",
                outline: "none", textAlign: "right",
                transition: "border-color 0.2s",
              }}
              onFocus={e => { e.target.style.borderColor = "rgba(241,225,148,0.6)"; }}
              onBlur={e  => { e.target.style.borderColor = "rgba(241,225,148,0.25)"; }}
            />
          </div>

          <div style={{ minHeight: "20px", marginBottom: "20px", fontSize: "0.8rem", fontWeight: 700 }}>
            {statusIcon}
          </div>

          <button
            type="submit"
            disabled={submitting || !username || status === "taken" || status === "invalid" || status === "checking"}
            style={{
              width: "100%", padding: "13px", borderRadius: "12px",
              background: "linear-gradient(135deg,#7a1520,#5B0E14)",
              border: "1.5px solid rgba(241,225,148,0.35)",
              color: "#F1E194", fontSize: "1rem", fontWeight: 800,
              fontFamily: "inherit", cursor: "pointer", transition: "opacity 0.2s",
              opacity: (submitting || !username || status === "taken" || status === "invalid" || status === "checking") ? 0.5 : 1,
            }}
          >
            {submitting ? "جاري الحفظ..." : "متابعة"}
          </button>
        </form>
      </div>
    </div>
  );
}
